import { modalController, toastController } from '@ionic/core';
import { Component, Event, Fragment, h, Prop, State } from '@stencil/core';
import QRCode from 'qrcode';
import { AuthService } from 'src/global/service/AuthService';
import { User } from '../../../global/models';
import { DateSelector } from 'src/global/ui/date-selector';
import { DateUtils } from 'common';

@Component({
  tag: 'share-link-modal',
})
export class ShareLinkModal {
  static async present(projectId: string, title: string, role: string, dismissedCallback: () => void): Promise<void> {
    const modal = await modalController.create({
      component: 'share-link-modal',
      componentProps: { projectId, shareTitle: title, role },
      cssClass: 'concourse-modal modal-height-auto',
    });

    modal.addEventListener('dismiss', () => {
      dismissedCallback();
      modal.dismiss();
    });

    return await modal.present();
  }

  @Prop() projectId: string;
  @Prop() shareTitle: string;
  @Prop() role: string;

  // Default expiration date is 30 days from now
  @State() expirationDate: string = new Date(DateUtils.currentMillis + DateUtils.MILLIS_PER_DAY * 30).toISOString();
  @State() shareLink: string;
  @State() shareEntireProject: boolean;
  @State() errorMessage: string;

  get user(): User {
    return globalThis.user;
  }

  get disableCheckbox(): boolean {
    return this.role == `p-${this.projectId}` || !this.user.hasOneOfRoles([`p-${this.projectId}`, `p-${this.projectId}-write`]);
  }

  @Event() dismiss;

  private sharedHash: string;

  async componentWillLoad() {
    this.shareEntireProject = this.role == `p-${this.projectId}` || this.projectId == 'demo';
  }

  onShareProjectChange(value: boolean) {
    this.shareEntireProject = value;
    this.generateShareLink();
  }

  async componentDidLoad() {}

  async showDateSelector() {
    DateSelector.presentModal(this.expirationDate, (date: string) => {
      this.expirationDate = date;
    });
  }

  private async generateShareLink() {
    //if (this.sharedHash == window.location.hash) return;
    this.sharedHash = window.location.hash;
    this.errorMessage = null;
    this.shareLink = null;
    try {
      const roles = this.shareEntireProject || this.projectId == 'demo' ? [`p-${this.projectId}`] : [this.role];
      const response = await AuthService.generateShareLink(this.projectId, this.sharedHash, roles, this.expirationDate);
      // the url must end with a slash to match the intent-filter in AndroidManifest.xml
      this.shareLink = `${globalThis.ccFrontEndUrl}?project=${this.projectId}&shared=${response.sharedLinkId}`;

      // need timeout so that the canvas is rendered before we try to draw the QR code
      setTimeout(() => {
        const qrCodeCanvas = document.getElementById('qrCodeCanvas') as HTMLCanvasElement;
        QRCode.toCanvas(qrCodeCanvas, this.shareLink, { width: 400 }, function (error) {
          if (error) console.error(error);
        });
      }, 300);
    } catch (error) {
      this.errorMessage = 'Error getting sharable url:' + error.message;
    }
  }

  async copyLink() {
    try {
      await navigator.clipboard.writeText(this.shareLink);
      toastController
        .create({
          message: 'Link copied to clipboard',
          duration: 1000,
        })
        .then(toast => toast.present());
    } catch (err) {
      this.errorMessage = 'Failed to copy to clipboard:' + err.message;
    }
  }

  render() {
    console.log('shareLink: ', this.shareLink);
    return (
      <Fragment>
        <modal-header modalTitle={this.shareTitle} onDismiss={() => this.dismiss.emit()}></modal-header>
        <ion-page class="modal-content">
          {this.errorMessage && <ion-text color="danger">{this.errorMessage}</ion-text>}
          {this.shareLink == null ? (
            <ion-grid class="cc-padding">
              <ion-row>
                <ion-col>
                  <ion-text>Generate a link which will give access to this project.</ion-text>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col>
                  Expiration Date: <b>{DateUtils.formatIso(this.expirationDate)}</b>
                  <ion-button onClick={() => this.showDateSelector()} style={{ marginLeft: '8px' }}>
                    Change
                  </ion-button>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col>
                  <ion-checkbox disabled={this.disableCheckbox} checked={this.shareEntireProject} onIonChange={e => this.onShareProjectChange(e.detail.checked)}>
                    Share access to entire project
                  </ion-checkbox>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col style={{ color: 'grey' }}>roles: {this.shareEntireProject ? `p-${this.projectId}` : this.role}</ion-col>
              </ion-row>
            </ion-grid>
          ) : (
            <ion-grid class="cc-padding">
              <ion-row>
                <ion-col>
                  <ion-text>Copy and share this link, or scan the QR code to open this page on another device.</ion-text>
                </ion-col>
              </ion-row>
              <ion-row>
                <ion-col class="center-content">
                  <canvas id="qrCodeCanvas" width="400" height="400"></canvas>
                </ion-col>
              </ion-row>
            </ion-grid>
          )}
        </ion-page>
        <ion-footer>
          <ion-toolbar>
            {this.shareLink == null ? (
              <ion-button data-testid="share-link-generate" expand="block" shape="round" onClick={() => this.generateShareLink()}>
                Generate Share Link
              </ion-button>
            ) : (
              [
                <ion-input class="ion-hide-md-down" data-testid="share-link-field" disabled value={this.shareLink}></ion-input>,
                <ion-button expand="block" shape="round" onClick={() => this.copyLink()}>
                  Copy Link
                </ion-button>,
              ]
            )}
          </ion-toolbar>
        </ion-footer>
      </Fragment>
    );
  }
}
